'use client';

import { Suspense, use } from 'react';
import ProductTileV2 from '@/components/molecules/product-tile-v2';
import { applyCtSnapshot } from '@/utils/mappers/applyCtSnapshot';
import type { ProductTileV2Props } from '@/components/molecules/product-tile-v2/types';
import type { CtProductSnapshot } from '@/utils/server/getCtSnapshotForSku';

interface Props extends Omit<ProductTileV2Props, 'priceUnavailable' | 'stockUnavailable'> {
  /**
   * Server-tastic-created per-SKU promise resolving to the CT snapshot.
   * Wrapped in a Suspense boundary here so each tile's price + stock +
   * Add-to-Cart area streams in independently. Undefined for anon
   * sessions with no scope; renders the "Unavailable" state.
   */
  ctPromise?: Promise<CtProductSnapshot | null>;
}

// Resolved branch — `use()` suspends until the CT snapshot resolves.
// Merges CT price + currency + stock into the tile's item and forwards
// the right `priceUnavailable` / `stockUnavailable` flags so the tile's
// existing gating logic handles the "Unavailable" / "Out of stock"
// surface when CT has no data for this SKU.
const Resolved = ({ ctPromise, item, ...rest }: Props & { ctPromise: Promise<CtProductSnapshot | null> }) => {
  const snapshot = use(ctPromise);
  // EWM-525 / EWM-598 / EWM-636 — single source for the snapshot → tile merge +
  // the show-price-vs-Unavailable decision, shared with the Featured carousel.
  // Merges the authoritative Standalone price + stock and clears any embedded
  // discount when the snapshot carries none (so no false was/now).
  const { item: mergedItem, priceUnavailable, stockUnavailable } = applyCtSnapshot(item, snapshot);

  return (
    <ProductTileV2
      {...rest}
      item={mergedItem}
      priceUnavailable={priceUnavailable}
      stockUnavailable={stockUnavailable}
      // EWM-926 — CT's quantity-break tiers, forwarded raw so the tile can
      // reprice live as the customer changes quantity. Only available on the
      // resolved branch: the pending/no-scope fallback has no snapshot, and
      // shows "Unavailable" anyway.
      priceBreaks={snapshot?.priceBreaks}
    />
  );
};

// Pending fallback — render the same tile shape with the "Unavailable" /
// "Out of stock" flags set. Keeps layout dimensions identical to the
// resolved branch, so the swap on resolution is a content change only —
// no reflow.
const PendingTile = (props: Props) => <ProductTileV2 {...props} priceUnavailable stockUnavailable />;

// EWM-926 — this wrapper is the PLP + Search grid's tile, and the only surface
// the quantity selector is in scope for. Defaulting it on HERE (rather than in
// the tile) keeps the Featured Products / Spare Parts carousels — which render
// `ProductTileV2` directly — on their existing single-row layout. A caller can
// still override it explicitly.
//
// EWM-1103 — the hover scale-up (EWM-187) is defaulted on here for the same
// reason: the carousels' react-slick track clips overflow, so a scaled tile
// there would be cut off at the edges. Both the Pending and Resolved branches
// get it, since both render from `props`.
const ProductTileWithCt = ({ showQuantitySelector = true, hoverScale = true, ...rest }: Props) => {
  const props: Props = { ...rest, showQuantitySelector, hoverScale };

  if (!props.ctPromise) {
    // No scope resolved (anon session with no store). Render the
    // permanent "Unavailable" state — no Suspense needed.
    return <PendingTile {...props} />;
  }
  return (
    <Suspense fallback={<PendingTile {...props} />}>
      <Resolved {...props} ctPromise={props.ctPromise} />
    </Suspense>
  );
};

export default ProductTileWithCt;
